// The top bar search: what a result row is, how it is labelled, and how the
// list is ordered. No JSX, so Node's own test runner can import it.
//
// The matching and the ranking are done in Postgres by global_search_v1
// (migration 0264). This file only decides presentation, and it never drops a
// row it does not recognise: an unknown kind is shown under its own name.
import type { ChipTone } from '../components/interviews/Chip'

export type SearchResult = {
  kind: string
  kind_label: string | null
  where_label: string | null
  ref_id: string | null
  title: string | null
  subtitle: string | null
  route: string | null
  rank: number | string | null
}

const KIND_TONES: Record<string, ChipTone> = {
  driver: 'green',
  applicant: 'brand',
  academy: 'amber',
  rejected: 'red',
  owner: 'neutral',
  vehicle: 'neutral',
  team: 'neutral',
}

export function kindTone(kind: string | null | undefined): ChipTone {
  return (kind && KIND_TONES[kind]) || 'neutral'
}

export function kindLabel(r: Pick<SearchResult, 'kind' | 'kind_label'>): string {
  if (r.kind_label && r.kind_label.trim()) return r.kind_label
  if (!r.kind) return 'Result'
  return r.kind.charAt(0).toUpperCase() + r.kind.slice(1)
}

// Rows arrive ranked. Keep the server's order, drop exact duplicates (the same
// record reached twice), and never throw on a row that is missing fields.
export function tidyResults(rows: unknown): SearchResult[] {
  if (!Array.isArray(rows)) return []
  const seen = new Set<string>()
  const out: SearchResult[] = []
  for (const raw of rows) {
    if (!raw || typeof raw !== 'object') continue
    const r = raw as SearchResult
    if (!r.kind || !r.title) continue
    const key = `${r.kind}:${r.ref_id ?? r.title}:${r.route ?? ''}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(r)
  }
  return out
}

// Arrow keys wrap, and a list with nothing in it has no active row.
export function moveActive(current: number, delta: number, length: number): number {
  if (length <= 0) return -1
  if (current < 0) return delta > 0 ? 0 : length - 1
  return (current + delta + length) % length
}

// A query worth sending: two characters, after trimming.
export function isSearchable(q: string): boolean {
  return q.trim().length >= 2
}
